import { type FC, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Badge — small inline status pill.
 *
 * Tones:
 *   neutral  — default slate
 *   success  — emerald
 *   warning  — amber
 *   danger   — red
 *   info     — brand-blue
 *   muted    — slate, dimmer
 *
 * With dot=true, shows a leading colored dot. Use for row-level statuses
 * (Active / Pending / Paused / Error) to match the dashboard spec.
 *
 * `subtle` drops the filled background for a borderless, text-only label —
 * use it for low-signal metadata (e.g. "system", "v3") so rows stay calm
 * and at most one filled badge competes for attention.
 */

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

export interface BadgeProps {
  tone?: Tone;
  dot?: boolean;
  subtle?: boolean;
  children: ReactNode;
  className?: string;
}

const toneClasses: Record<Tone, { bg: string; text: string; dot: string }> = {
  neutral: { bg: 'bg-slate-900/[0.06]', text: 'text-text-secondary', dot: 'bg-slate-500' },
  success: { bg: 'bg-emerald-500/12',   text: 'text-emerald-700',    dot: 'bg-emerald-500' },
  warning: { bg: 'bg-amber-500/14',     text: 'text-amber-700',      dot: 'bg-amber-500' },
  danger:  { bg: 'bg-red-500/12',       text: 'text-red-700',        dot: 'bg-red-500' },
  info:    { bg: 'bg-brand-blue/[0.08]',text: 'text-brand-blue',     dot: 'bg-brand-blue' },
  muted:   { bg: 'bg-slate-900/[0.04]', text: 'text-text-tertiary',  dot: 'bg-slate-300' },
};

export const Badge: FC<BadgeProps> = ({ tone = 'neutral', dot = false, subtle = false, children, className }) => {
  const t = toneClasses[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md text-caption font-medium',
        subtle ? 'px-0 text-text-muted' : cn('px-2 py-0.5', t.bg, t.text),
        className,
      )}
    >
      {dot && <span className={cn('w-1.5 h-1.5 rounded-full', subtle ? t.dot : t.dot)} aria-hidden />}
      {children}
    </span>
  );
};

export default Badge;
